import type {
  RoyaltyReportOut,
  RoyaltyStatementOut,
} from '@/api/generated/schemas';

/**
 * The share each publisher of the group holds right now — its highest
 * version. Statements are append-only, so the report's amount is the sum of
 * exactly these rows. Largest share first.
 */
export const currentStatements = (
  statements: readonly RoyaltyStatementOut[]
): RoyaltyStatementOut[] => {
  const latest = new Map<number, RoyaltyStatementOut>();
  for (const s of statements) {
    const seen = latest.get(s.publisher_id);
    if (!seen || s.version > seen.version) latest.set(s.publisher_id, s);
  }
  return [...latest.values()].sort(
    (a, b) => Number(b.amount) - Number(a.amount)
  );
};

/** Superseded versions of one publisher's share, newest first. */
export const statementHistory = (
  statements: readonly RoyaltyStatementOut[],
  current: RoyaltyStatementOut
): RoyaltyStatementOut[] =>
  statements
    .filter(
      (s) => s.publisher_id === current.publisher_id && s.id !== current.id
    )
    .sort((a, b) => b.version - a.version);

/** Full copies across the group — the sum over the current shares. */
export const reportUnitsCount = (
  record: Pick<RoyaltyReportOut, 'statements'>
): number =>
  currentStatements(record.statements).reduce(
    (sum, s) => sum + Number(s.units_count),
    0
  );
